import React, { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Table } from 'reactstrap';
import { OAuthApplicationModel } from '../../../../../shared/oauth';
import { PermissionName } from '../../../../../shared/permissions';
import { ActionButtonModal, MaterialIcon } from '../../../../components';
import { useTranslation } from '../../../../translations';
import { omit } from '../../../../utils';
import { captureError } from '../../../../utils/errorHandling';
import { OAuthManageModal } from './OAuthModal';

interface OAuthTableProps {
  readonly applications: OAuthApplicationModel[];
  readonly permissions: PermissionName[];
  readonly onChange: () => void;
}

export const OAuthTable: FC<OAuthTableProps> = ({ applications, permissions, onChange }) => {
  const { ts } = useTranslation();
  const [editEntry, setEditEntry] = useState<OAuthApplicationModel | null>(null);
  if (applications.length === 0) {
    return <p>{ts('no_applications_created')}</p>;
  }

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>#</th>
            <th>{ts('name')}</th>
            <th>{ts('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {applications.map((app) => (
            <tr key={app.id}>
              <td>{app.id}</td>
              <td>{app.name}</td>
              <td>
                <Button
                  color="primary"
                  id={`app-${app.id}-edit`}
                  onClick={() => {
                    setEditEntry(app);
                  }}
                >
                  {ts('common.edit')}
                </Button>
                <ActionButtonModal
                  actionContent="common.delete"
                  buttonContent={<MaterialIcon name="delete" />}
                  className="indent"
                  color="danger"
                  id={`delete-${app.id}`}
                  onComplete={async () => {
                    await api.deleteOAuthApplication(app.id);
                    toast.success(ts('application_deleted'));
                    onChange();
                  }}
                  title="common.delete_application"
                >
                  {ts('are_you_sure_you_want_to_delete')} {app.name}?
                </ActionButtonModal>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {editEntry && (
        <EditModal
          close={() => {
            setEditEntry(null);
          }}
          permissions={permissions}
          refresh={onChange}
          startApp={editEntry}
        />
      )}
    </>
  );
};

const EditModal: FC<{
  readonly startApp: OAuthApplicationModel;
  readonly permissions: PermissionName[];
  readonly refresh: () => void;
  readonly close: () => void;
}> = ({ startApp, permissions, close, refresh }) => {
  const [app, setApp] = useState<OAuthApplicationModel>(startApp);
  const { ts } = useTranslation();

  const onComplete = useCallback(
    async (cancelled: boolean) => {
      close();
      if (cancelled) {
        return;
      }

      try {
        await api.updateOAuthApplication(app.id, omit(app, 'id'));

        toast.success(ts('application_updated'));
        refresh();
      } catch (error) {
        captureError(error as Error);
      }
    },
    [app, close, refresh, ts],
  );

  return (
    <OAuthManageModal
      app={app}
      onClose={onComplete}
      open
      permissions={permissions}
      setApp={setApp}
    />
  );
};
